import { useState } from 'react';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useQuery } from '@tanstack/react-query';

import { comoErroDaApi } from '../../../shared/api/erros';
import { EstadoDeCarregamento } from '../../../shared/ui/EstadoDeCarregamento';
import { EstadoDeErro } from '../../../shared/ui/EstadoDeErro';
import { EstadoVazio } from '../../../shared/ui/EstadoVazio';
import type { EstadoDaFonte } from '../api/tipos';
import { opcoesDaFila } from './api/revisao';
import { ROTULOS_DE_CONFIANCA, ROTULOS_DE_TIPO_DE_REGRA, type RegraDaFila, type TipoDeRegra } from './api/tipos';

/**
 * A fila vazia tem causas opostas: "ainda não chegou nada" e "tudo já foi decidido". Dizer qual,
 * em vez de um "nada para revisar" único, evita que a pessoa ache que algo quebrou.
 */
const VAZIO_DA_FILA: Record<EstadoDaFonte, { titulo: string; descricao: string }> = {
  recebida: { titulo: 'Ainda sem regras', descricao: 'O documento ainda não foi lido.' },
  texto_extraido: { titulo: 'Ainda sem regras', descricao: 'O documento foi lido; as regras ainda vão ser propostas.' },
  mapeada: { titulo: 'Ainda sem regras', descricao: 'As regras ainda estão sendo propostas.' },
  regras_extraidas: { titulo: 'Nada esperando decisão', descricao: 'Nenhuma regra está pendente agora. O próximo passo é ativar a fonte.' },
  em_revisao: { titulo: 'Nada esperando decisão', descricao: 'Nenhuma regra está pendente agora. O próximo passo é ativar a fonte.' },
  ativa: { titulo: 'Fonte ativa', descricao: 'As regras já foram decididas e a fonte está valendo.' },
  rejeitada: { titulo: 'Fonte rejeitada', descricao: 'Esta fonte foi recusada; não há regras a revisar.' },
};

/** Tom por confiança — reaproveita `palette.sla` (mesmo desvio de `SeloDeEstadoDaFonte`, `TP-049` `DI14`). */
const TOM_DA_CONFIANCA: Record<RegraDaFila['confianca'], 'ok' | 'atencao' | 'estourado'> = {
  alta: 'ok',
  media: 'atencao',
  baixa: 'estourado',
};

function SeloDeConfianca({ confianca }: { confianca: RegraDaFila['confianca'] }): React.JSX.Element {
  return (
    <Chip
      size="small"
      label={ROTULOS_DE_CONFIANCA[confianca]}
      sx={(tema) => {
        const tom = tema.palette.sla[TOM_DA_CONFIANCA[confianca]];
        return { backgroundColor: tom.fundo, color: tom.tinta, border: `1px solid ${tom.borda}`, fontWeight: 600 };
      }}
    />
  );
}

/**
 * Aprovação em lote de um tipo (`TP-050`, T8): a API só aprova, dentro do
 * tipo, as regras de confiança ALTA — o resto segue pendente para revisão
 * individual. O botão fica habilitado sempre que o tipo aparece na fila
 * (mesmo sem nenhuma de confiança alta): a tentativa é inofensiva (devolve
 * `aprovadas: 0`) e a pessoa vê a contagem real, em vez de a tela adivinhar.
 */
function LinhaDeLotePorTipo({
  tipo,
  quantidade,
  aoAprovarLote,
}: {
  tipo: TipoDeRegra;
  quantidade: number;
  aoAprovarLote: (tipo: TipoDeRegra) => Promise<number>;
}): React.JSX.Element {
  const [emCurso, definirEmCurso] = useState(false);
  const [erro, definirErro] = useState<string | null>(null);

  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <Chip size="small" variant="outlined" label={`${ROTULOS_DE_TIPO_DE_REGRA[tipo]}: ${String(quantidade)}`} />
      <Button
        size="small"
        variant="text"
        disabled={emCurso}
        aria-label={`Aprovar em lote (confiança alta): ${ROTULOS_DE_TIPO_DE_REGRA[tipo]}`}
        onClick={() => {
          definirEmCurso(true);
          definirErro(null);
          aoAprovarLote(tipo)
            .then(() => undefined)
            .catch((causa: unknown) => {
              definirErro(comoErroDaApi(causa).message);
            })
            .finally(() => {
              definirEmCurso(false);
            });
        }}
      >
        Aprovar em lote (confiança alta)
      </Button>
      {erro !== null ? (
        <Typography variant="caption" color="error.main">
          {erro}
        </Typography>
      ) : null}
    </Stack>
  );
}

/**
 * A fila de regras extraídas de uma fonte (`TP-050`, T9): contagem por tipo
 * e por confiança, e a lista na ordem que a API já devolve —
 * `nao_classificavel` > `baixa` > `media` > `alta` (T8).
 */
export function FilaDeRevisao({
  fonteId,
  estadoDaFonte,
  regraSelecionadaId,
  aoSelecionar,
  aoAprovarLote,
}: {
  fonteId: string;
  estadoDaFonte: EstadoDaFonte;
  regraSelecionadaId: string | null;
  aoSelecionar: (regra: RegraDaFila) => void;
  aoAprovarLote: (tipo: TipoDeRegra) => Promise<number>;
}): React.JSX.Element {
  const consulta = useQuery(opcoesDaFila(fonteId));
  const [avisoDoLote, definirAvisoDoLote] = useState<string | null>(null);
  const aprovarLoteComAviso = (tipo: TipoDeRegra): Promise<number> =>
    aoAprovarLote(tipo).then((aprovadas) => {
      definirAvisoDoLote(`${String(aprovadas)} aprovada(s) em lote: ${ROTULOS_DE_TIPO_DE_REGRA[tipo]}.`);
      return aprovadas;
    });

  if (consulta.isPending) return <EstadoDeCarregamento linhas={5} />;

  if (consulta.isError) {
    return (
      <EstadoDeErro
        erro={consulta.error}
        contexto="Não foi possível carregar a fila de revisão"
        aoReprocessar={() => {
          void consulta.refetch();
        }}
      />
    );
  }

  if (consulta.data.length === 0) {
    return (
      <Stack spacing={1}>
        <EstadoVazio titulo={VAZIO_DA_FILA[estadoDaFonte].titulo} descricao={VAZIO_DA_FILA[estadoDaFonte].descricao} />
        {avisoDoLote !== null ? (
          <Typography variant="caption" color="text.secondary" role="status">
            {avisoDoLote}
          </Typography>
        ) : null}
      </Stack>
    );
  }

  const porTipo = new Map<TipoDeRegra, number>();
  const porConfianca = new Map<string, number>();
  for (const regra of consulta.data) {
    porTipo.set(regra.tipo, (porTipo.get(regra.tipo) ?? 0) + 1);
    porConfianca.set(regra.confianca, (porConfianca.get(regra.confianca) ?? 0) + 1);
  }

  return (
    <Stack spacing={1.5}>
      <Typography variant="h3">Fila de revisão</Typography>

      <Stack direction="row" spacing={0.5} flexWrap="wrap" aria-label="Contagem por confiança">
        {(['baixa', 'media', 'alta'] as const)
          .filter((confianca) => (porConfianca.get(confianca) ?? 0) > 0)
          .map((confianca) => (
            <Chip
              key={confianca}
              size="small"
              label={`${ROTULOS_DE_CONFIANCA[confianca]}: ${String(porConfianca.get(confianca) ?? 0)}`}
            />
          ))}
      </Stack>

      <Stack spacing={0.5} aria-label="Aprovação em lote por tipo">
        {[...porTipo.entries()].map(([tipo, quantidade]) => (
          <LinhaDeLotePorTipo key={tipo} tipo={tipo} quantidade={quantidade} aoAprovarLote={aprovarLoteComAviso} />
        ))}
        {avisoDoLote !== null ? (
          <Typography variant="caption" color="text.secondary" role="status">
            {avisoDoLote}
          </Typography>
        ) : null}
      </Stack>

      <List aria-label="Regras aguardando revisão" dense>
        {consulta.data.map((regra) => (
          <ListItemButton
            key={regra.id}
            selected={regra.id === regraSelecionadaId}
            onClick={() => {
              aoSelecionar(regra);
            }}
          >
            <ListItemText
              primary={
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="body2">{ROTULOS_DE_TIPO_DE_REGRA[regra.tipo]}</Typography>
                  <SeloDeConfianca confianca={regra.confianca} />
                  {!regra.trechoLocalizado ? <Chip size="small" color="warning" label="Trecho não localizado" /> : null}
                </Stack>
              }
              secondary={`Página ${String(regra.pagina)}`}
            />
          </ListItemButton>
        ))}
      </List>
    </Stack>
  );
}
